import { Alert, Autocomplete, Button, Group, NumberInput, Select, SimpleGrid, Stack, Switch, Text, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { http } from "../../api/http";
import { useT } from "../../i18n/script";
import { KoParcelsFields, type ParcelCheck } from "../dms/DmsPage";

interface Item { code: string; name: string; parent_code?: string | null }
const useItems = (code: string) =>
  useQuery({ queryKey: ["codelist", code], staleTime: 600_000, queryFn: () => http.get<Item[]>(`/api/v1/codelists/${code}/items`) });

/**
 * Novi voćnjak kroz aplikaciju (isto što i obrazac „Voćnjaci“ u uvozu): granica je unija izabranih katastarskih
 * čestica iz evidencije, ostalo se bira iz šifarnika ili upisuje slobodno.
 */
export function NewOrchardForm({ holdingId, onDone }: { holdingId: number; onDone: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const nav = useNavigate();
  const species = useItems("vrsta_voca");
  const cultivars = useItems("sorta");
  const rootstocks = useItems("podloga");
  const training = useItems("uzgojni_oblik");
  const [f, setF] = useState({ name: "", species: "JABUKA", cultivar: "", rootstock: "", training_system: "",
    planting_year: null as number | null, row_spacing_m: null as number | null, tree_spacing_m: null as number | null,
    organic: false, hail_net: false });
  const [ko, setKo] = useState<string | null>(null);
  const [kc, setKc] = useState<string[]>([]);
  const [evid, setEvid] = useState<string | null>("KZ");
  const koId = ko && !ko.startsWith("free:") ? Number(ko) : null;
  const check = useQuery({ queryKey: ["kc-check", ko, kc.join(",")], enabled: kc.length > 0, staleTime: 60_000,
    queryFn: () => http.get<ParcelCheck[]>("/api/v1/dms/parcel-check", { ko_id: koId, parcels: kc.join(",") }) });
  const ids = (check.data ?? []).filter((c) => c.found && c.parcel_id).map((c) => c.parcel_id!) ;
  const unknown = (check.data ?? []).filter((c) => !c.found).map((c) => c.broj);
  const spName = species.data?.find((s) => s.code === f.species)?.name ?? f.species;
  const sorte = (cultivars.data ?? []).filter((c) => !c.parent_code || c.parent_code === f.species).map((c) => c.name);
  const save = useMutation({
    mutationFn: () => http.post<{ id: number }>("/api/v1/orchards", {
      ...f, holding_id: holdingId, cultivar: f.cultivar || null, rootstock: f.rootstock || null, training_system: f.training_system || null,
      name: f.name.trim() || `${spName}${f.cultivar ? ` ${f.cultivar}` : ""} — ${kc.join(", ")}`,
      cadastral_parcel_ids: ids, cadastral_ref: koId ? undefined : [ko?.slice(5), kc.join(", ")].filter(Boolean).join(" "),
    }),
    onSuccess: (r) => {
      notifications.show({ color: "green", message: t("Voćnjak je upisan") });
      qc.invalidateQueries({ queryKey: ["producer"] });
      onDone();
      nav(`/vocnjaci/${r.id}`);
    },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  const set = (k: keyof typeof f) => (v: unknown) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Stack>
      <KoParcelsFields ko={ko} setKo={setKo} parcels={kc} setParcels={setKc} evid={evid} setEvid={setEvid} />
      {kc.length > 0 && !ids.length && !check.isFetching && (
        <Alert color="orange">{t("Nijedna od upisanih čestica nije u evidenciji — granicu voćnjaka nije moguće odrediti. Izaberite čestice iz liste ili uvezite granicu iz GIS fajla.")}</Alert>
      )}
      {ids.length > 0 && unknown.length > 0 && (
        <Text size="xs" c="orange">{t("Granica se pravi od prepoznatih čestica; nisu prepoznate")}: {unknown.join(", ")}</Text>
      )}
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Select label={t("Vrsta voća")} required searchable allowDeselect={false} value={f.species} onChange={(v) => set("species")(v ?? "JABUKA")}
          data={(species.data ?? []).map((s) => ({ value: s.code, label: s.name }))} />
        <Autocomplete label={t("Sorta")} value={f.cultivar} onChange={set("cultivar")} data={sorte} limit={30}
          description={t("Iz šifarnika ili slobodan upis")} />
        <Autocomplete label={t("Podloga")} value={f.rootstock} onChange={set("rootstock")} data={(rootstocks.data ?? []).map((r) => r.name)} />
        <Autocomplete label={t("Uzgojni oblik")} value={f.training_system} onChange={set("training_system")} data={(training.data ?? []).map((r) => r.name)} />
        <NumberInput label={t("Godina sadnje")} min={1950} max={new Date().getFullYear() + 1} value={f.planting_year ?? ""}
          onChange={(v) => set("planting_year")(v === "" ? null : Number(v))} />
        <Group grow>
          <NumberInput label={t("Razmak redova (m)")} decimalScale={2} min={0.5} max={15} value={f.row_spacing_m ?? ""}
            onChange={(v) => set("row_spacing_m")(v === "" ? null : Number(v))} />
          <NumberInput label={t("U redu (m)")} decimalScale={2} min={0.2} max={15} value={f.tree_spacing_m ?? ""}
            onChange={(v) => set("tree_spacing_m")(v === "" ? null : Number(v))} />
        </Group>
      </SimpleGrid>
      <TextInput label={t("Naziv voćnjaka")} placeholder={`${spName}${f.cultivar ? ` ${f.cultivar}` : ""} — ${kc.join(", ") || "KČ"}`}
        value={f.name} onChange={(e) => set("name")(e.currentTarget.value)} />
      <Group>
        <Switch label={t("Organska proizvodnja")} checked={f.organic} onChange={(e) => set("organic")(e.currentTarget.checked)} />
        <Switch label={t("Protivgradna mreža")} checked={f.hail_net} onChange={(e) => set("hail_net")(e.currentTarget.checked)} />
      </Group>
      <Group justify="flex-end">
        <Button variant="default" onClick={onDone}>{t("Odustani")}</Button>
        <Button loading={save.isPending} disabled={!ids.length} onClick={() => save.mutate()}>{t("Upiši voćnjak")}</Button>
      </Group>
    </Stack>
  );
}
